import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { API_URL } from "../../config";
import { Pie } from "react-chartjs-2";
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend
} from "chart.js";

ChartJS.register(ArcElement, Tooltip, Legend);

function Dashboard() {
    const navigate = useNavigate();
    const location = useLocation();
    const token = localStorage.getItem("access_token");
    const [upcomingMissions, setUpcomingMissions] = useState([]);
    const [loadingMissions, setLoadingMissions] = useState(false);

    // State to hold the statistics
    const [stats, setStats] = useState({
        activeUsers: 0,
        upcomingEvents: 0,
        pastEvents: 0,
        upcomingMissions: 0,
        pastMissions: 0,
        schoolsCount: 0,
        missionBooksCount: 0,
        genderDistribution: {
            male: 0,
            female: 0,
            unspecified: 0
        },
        schoolType: {
            girls: 0,
            boys: 0,
            mixed: 0
        },
        missionBooksAvailability: {
            available: 0,
            unavailable: 0,
            unspecified: 0
        },
        missionFeedback: {
            totalFeedbacks: 0,
            totalMissions: 0,
            feedbackPercentage: 0
        },
        converts: {
            totalConverts: 0,
            totalFeedbacks: 0,
            averagePerMission: 0
        }
    });

    const fetchDashboardStats = useCallback(async () => {
        try {
            if (!token) {
                navigate("/logout", { state: { from: location } });
                return;
            }

            const response = await fetch(`${API_URL}/dashboard-stats`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json"
                }
            });

            if (response.ok) {
                const data = await response.json();
                setStats(data);
            } else if (response.status === 401) {
                navigate("/logout", { state: { from: location } });
            } else {
                console.error("Failed to fetch dashboard stats", response.statusText);
            }
        } catch (error) {
            console.error("Error fetching dashboard stats:", error);
        }
    }, [token, navigate, location]);

    const fetchUpcomingMissions = useCallback(async () => {
        setLoadingMissions(true);
        try {
            const response = await fetch(`${API_URL}/missions/upcoming?days=7`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setUpcomingMissions(data);
            } else if (response.status === 401) {
                navigate("/logout", { state: { from: location } });
            }
        } catch (error) {
            console.error("Error fetching upcoming missions:", error);
        } finally {
            setLoadingMissions(false);
        }
    }, [token, navigate, location]);

    useEffect(() => {
        fetchDashboardStats();
        fetchUpcomingMissions();
    }, [fetchDashboardStats, fetchUpcomingMissions]);

    const genderData = {
        labels: ["Male", "Female", "Unspecified"],
        datasets: [
            {
                data: [
                    stats.genderDistribution.male,
                    stats.genderDistribution.female,
                    stats.genderDistribution.unspecified
                ],
                backgroundColor: ["#36A2EB", "#FF6384", "#FFCE56"],
                hoverBackgroundColor: ["#36A2EB", "#FF6384", "#FFCE56"]
            }
        ]
    };

    const schoolTypeData = {
        labels: ["Boys", "Girls", "Mixed"],
        datasets: [
            {
                data: [
                    stats.schoolType.boys,
                    stats.schoolType.girls,
                    stats.schoolType.mixed
                ],
                backgroundColor: ["#36A2EB", "#FF6384", "#FFCE56"],
                hoverBackgroundColor: ["#36A2EB", "#FF6384", "#FFCE56"]
            }
        ]
    };

    const missionBooksAvailability = {
        labels: ["Available", "UnAvailable", "Unspecified"],
        datasets: [
            {
                data: [
                    stats.missionBooksAvailability.available,
                    stats.missionBooksAvailability.unavailable,
                    stats.missionBooksAvailability.unspecified
                ],
                backgroundColor: ["#36A2EB", "#FF6384", "#FFCE56"],
                hoverBackgroundColor: ["#36A2EB", "#FF6384", "#FFCE56"]
            }
        ]
    };

    return (
        <div>
            <main>
                                <div className="columns">
                    <div className="column is-6">
                        <div className="card p-4">
                            <div className="level mb-2">
                                <div className="level-left">
                                    <p className="dash-card-title">📅 Upcoming Missions (Next 7 Days)</p>
                                </div>
                                <div className="level-right">
                                    <Link to="/dashboard/missions" className="button is-small is-light">
                                        View all
                                    </Link>
                                </div>
                            </div>
                            <div className="card-content p-0 pt-2">
                                {loadingMissions ? (
                                    <p className="has-text-grey">Loading...</p>
                                ) : upcomingMissions.length === 0 ? (
                                    <p className="has-text-grey">No missions scheduled in the next 7 days.</p>
                                ) : (
                                    <div className="notification-list">
                                        {upcomingMissions.map(mission => (
                                            <div key={mission.id} className="notification-item mb-2">
                                                <div className="columns is-mobile is-vcentered is-multiline mb-0">
                                                    <div className="column is-8">
                                                        <Link
                                                            to={`/dashboard/missions/${mission.id}/details`}
                                                            className="has-text-weight-semibold has-text-dark"
                                                        >
                                                            {mission.school?.name || 'Unknown School'}
                                                        </Link>
                                                        <span className="tag is-light ml-2">
                                                            {mission.mission_type?.name || 'Mission'}
                                                        </span>
                                                        <div className="has-text-grey is-size-7 mt-1">
                                                            📅 {new Date(mission.start_date).toLocaleDateString('en-GB')}
                                                            {mission.start_time && ` ⏰ ${mission.start_time}`}
                                                        </div>
                                                    </div>
                                                    <div className="column is-4 has-text-right">
                                                        <span className={`tag ${mission.status === 'Active' ? 'is-success' : 'is-warning'}`}>
                                                            {mission.status || 'Scheduled'}
                                                        </span>
                                                    </div>
                                                </div>
                                               
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="columns">
                    <div className="column is-3">
                        <div className="card p-4">
                            <p className="dash-card-title">ACTIVE USERS</p>
                            <div className="card-content has-text-weight-semibold pl-0 pr-0">
                                <p className="title">{stats.activeUsers}</p>
                                <hr />
                            </div>
                        </div>
                    </div>

                    <div className="column is-3">
                        <div className="card p-4">
                            <p className="dash-card-title"><Link to="/dashboard/missions" className="no_deco_link">UPCOMING MISSIONS</Link> </p>
                            <div className="card-content has-text-weight-semibold pl-0 pr-0">
                                <Link to="/dashboard/missions" className="no_deco_link"><p className="title">{stats.upcomingMissions}</p></Link>
                                <hr />
                            </div>
                        </div>
                    </div>

                    <div className="column is-3">
                        <div className="card p-4">
                            <p className="dash-card-title"><Link to="/dashboard/missions" className="no_deco_link">PAST MISSIONS</Link> </p>
                            <div className="card-content has-text-weight-semibold pl-0 pr-0">
                                <Link to="/dashboard/missions" className="no_deco_link"><p className="title">{stats.pastMissions}</p></Link>
                                <hr />

                            </div>
                        </div>
                    </div>

                    <div className="column is-3">
                        <div className="card p-4">
                            <p className="dash-card-title"><Link to="/dashboard/missions" className="no_deco_link">MISSION FEEDBACK</Link> </p>
                            <div className="card-content has-text-weight-semibold pl-0 pr-0">
                                <Link to="/dashboard/missions" className="no_deco_link">
                                    <div className="">
                                        <span className="title">{stats?.missionFeedback?.totalFeedbacks}</span>
                                        <span className="is-size-6 has-text-grey mr-2">/ {stats.pastMissions}</span>
                                        <span className="is-size-7  has-text-weight-bold">
                                            ({stats?.missionFeedback?.feedbackPercentage}%)
                                        </span>
                                    </div>
                                </Link>
                                <hr />
                            </div>
                        </div>
                    </div>


                </div> {/* End columns */}

                <div className="columns">
                    <div className="column is-3">
                        <div className="card p-4">
                            <p className="dash-card-title"><Link to="/dashboard/missions" className="no_deco_link">CONVERTS</Link> </p>
                            <div className="card-content has-text-weight-semibold pl-0 pr-0">
                                <Link to="/dashboard/missions" className="no_deco_link">

                                    <span className="title">{stats?.converts?.totalConverts} </span>
                                    <span className="is-size-6 has-text-grey mr-2">from {stats?.converts?.totalFeedbacks} missions</span>
                                    <span className="is-size-7  has-text-weight-bold"> <br />
                                        (Average converts per mission: {stats?.converts?.averagePerMission} )
                                    </span>

                                </Link>
                                <hr />
                            </div>
                        </div>
                    </div>

                    <div className="column is-3">
                        <div className="card p-4">
                            <p className="dash-card-title"><Link to="/dashboard/events" className="no_deco_link">UPCOMING EVENTS</Link> </p>
                            <div className="card-content has-text-weight-semibold pl-0 pr-0">
                                <Link to="/dashboard/events" className="no_deco_link"><p className="title">{stats.upcomingEvents}</p></Link>
                                <hr />
                            </div>
                        </div>
                    </div>

                    <div className="column is-3">
                        <div className="card p-4">
                            <p className="dash-card-title"> <Link to="/dashboard/events" className="no_deco_link">PAST EVENTS</Link></p>
                            <div className="card-content has-text-weight-semibold pl-0 pr-0">
                                <Link to="/dashboard/events" className="no_deco_link"><p className="title">{stats.pastEvents}</p></Link>
                                <hr />
                            </div>
                        </div>
                    </div>

                    <div className="column is-3">
                        <div className="card p-4">
                            <p className="dash-card-title"> <Link to="/dashboard/schools" className="no_deco_link">SCHOOLS</Link></p>
                            <div className="card-content has-text-weight-semibold pl-0 pr-0">
                                <Link to="/dashboard/schools" className="no_deco_link"><p className="title">{stats.schoolsCount}</p></Link>
                                <hr />
                            </div>
                        </div>
                    </div>


                </div> {/* End columns */}

                <div className="columns">

                    <div className="column is-3">
                        <div className="card p-4">
                            <p className="dash-card-title">CFCM MEMBERS BY GENDER</p>
                            <div className="card-content has-text-centered">
                                <Pie data={genderData} />
                            </div>
                        </div>
                    </div>

                    <div className="column is-3">
                        <div className="card p-4">
                            <p className="dash-card-title">School Type</p>
                            <div className="card-content has-text-centered">
                                <Pie data={schoolTypeData} />
                            </div>
                        </div>
                    </div>

                    <div className="column is-3">
                        <div className="card p-4">
                            <p className="dash-card-title"> <Link to="/dashboard/books" className="no_deco_link">Mission Books Availability</Link></p>
                            <div className="card-content has-text-centered">
                                <Pie data={missionBooksAvailability} />
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}

export default Dashboard;
